import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { APP, NAV, ROUTES, activeApp, canAccessApp, visibleChildren } from '../config';
import AppSwitcher from '@taskydo/web-core/AppSwitcher.jsx';
import { navForProduct } from '@taskydo/web-core/paths.js';
import { getConfig } from '@taskydo/web-core/runtime.js';

import { useAuth } from '../lib/auth';
import Avatar from './Avatar.jsx';
import {
  AppIcon,
  BrandMark,
  ChatBubbleIcon,
  ChevronDownIcon,
  LogoutIcon,
  SupportIcon,
} from './Icons';
import NotificationBell from './NotificationBell.jsx';
import UserMenu from './UserMenu.jsx';





/**
 * Horizontally-scrollable tab strip used in the top bar.
 *
 * Why this exists
 * ---------------
 * Modules with many sub-pages (Pharma SFA has ~9 today, will grow)
 * push the top-bar tab list past the available width on common
 * laptop screens.  ``overflow-x: auto`` alone hides the right-hand
 * tabs without any visual clue, so users couldn't reach pages like
 * "Sales" or "Assistant".
 *
 * This component renders the same ``<NavLink>`` row as before, but
 * wraps it in a ``.ty-tabs-scroller`` flex shell with two chevron
 * buttons.  We measure overflow on every relevant event
 * (resize / scroll / route change / tab list change) and toggle two
 * CSS classes so the chevrons + edge-fade gradients only show when
 * there's actually content to scroll towards.
 *
 * The component is generic — any future page with a long tab list
 * could reuse it — but right now the only caller is ``AppShell``.
 *
 * A tab may be a second-level menu (``tab.children``) instead of a
 * link.  Pharma SFA uses that for Master.  The panel is portaled to
 * ``document.body`` because the tab track clips overflow — a menu
 * drawn inside ``.ty-tabs`` would be cut off.
 */
function tabKey(tab) {
  return tab.to || `group:${tab.label}`;
}

function peerPaths(tab) {
  return (tab.children || []).map((child) => child.to).filter(Boolean);
}

/**
 * A menu item is active on its own path and on deeper pages under it
 * (a saved DCR at ``/pharma/dcr/15`` still belongs to My DCR).  A longer
 * sibling wins when the path is exactly that sibling — ``/pharma/dcr/new``
 * lights New DCR and not My DCR.
 */
function itemIsActive(pathname, to, peers) {
  if (!to || !pathname) return false;
  if (pathname === to) return true;
  if (!pathname.startsWith(`${to}/`)) return false;
  return !peers.some((other) => (
    other !== to
    && other.startsWith(`${to}/`)
    && (pathname === other || pathname.startsWith(`${other}/`))
  ));
}

function groupIsActive(tab, pathname) {
  const peers = peerPaths(tab);
  return peers.some((to) => itemIsActive(pathname, to, peers));
}

function linkShouldEnd(pathname, to, peers, homeEnd) {
  if (homeEnd) return true;
  return peers.some((other) => (
    other !== to
    && other.startsWith(`${to}/`)
    && (pathname === other || pathname.startsWith(`${other}/`))
  ));
}

function TabMenu({ tab, isTabEnd, open, onToggle, onClose }) {
  const location = useLocation();
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [box, setBox] = useState(null);
  const active = groupIsActive(tab, location.pathname);
  const peers = peerPaths(tab);

  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 280;
    const margin = 8;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin);
    }
    const top = rect.bottom + 4;
    const maxHeight = Math.max(140, window.innerHeight - top - margin);
    setBox({ top, left, width, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      const target = event.target;
      if (btnRef.current && btnRef.current.contains(target)) return;
      if (panelRef.current && panelRef.current.contains(target)) return;
      onClose();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const panel = open && box ? createPortal(
    <div
      ref={panelRef}
      className="ty-tab-menu__panel"
      role="menu"
      aria-label={tab.label}
      style={{ top: box.top, left: box.left, width: box.width, maxHeight: box.maxHeight }}
    >
      {tab.children.map((child) => (
        <NavLink
          key={child.to}
          to={child.to}
          end={linkShouldEnd(location.pathname, child.to, peers, isTabEnd(child.to))}
          role="menuitem"
          className={({ isActive }) => (
            'ty-tab-menu__item' + (isActive ? ' ty-tab-menu__item--active' : '')
          )}
          onClick={onClose}
        >
          <span>{child.label}</span>
          {child.hint ? <span className="ty-tab-menu__hint">{child.hint}</span> : null}
        </NavLink>
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="ty-tab-menu">
      <button
        ref={btnRef}
        type="button"
        className={'ty-tab ty-tab--menu' + (active ? ' ty-tab--active' : '')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        {tab.label}
        <span className="ty-tab__caret" aria-hidden>
          <ChevronDownIcon size={14} />
        </span>
      </button>
      {panel}
    </div>
  );
}

function ScrollableTabs({ tabs, isTabEnd, ariaLabel }) {
  const trackRef    = useRef(null);
  const scrollerRef = useRef(null);

  // Re-evaluate which side(s) overflow, and on a phone whether the
  // tab list is short enough to wrap onto extra rows.  Wrapping is
  // capped so a module with dozens of pages (WhatsApp, Pharma) stays
  // a single swipeable row instead of eating the screen.
  const recomputeOverflow = useCallback(() => {
    const track    = trackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return;
    const SLOP = 1;   // sub-pixel rounding tolerance
    const WRAP_MAX_ROWS = 4;
    const narrow = window.matchMedia('(max-width: 600px)').matches;
    let wrap = false;
    if (narrow) {
      const available = scroller.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      let rows = 1;
      let used = 0;
      for (const el of track.children) {
        const w = el.getBoundingClientRect().width;
        if (w <= 0) continue;
        const next = used === 0 ? w : used + gap + w;
        if (used > 0 && next > available + SLOP) {
          rows += 1;
          used = w;
        } else {
          used = next;
        }
      }
      wrap = rows > 1 && rows <= WRAP_MAX_ROWS;
    }
    track.classList.toggle('ty-tabs--wrap', wrap);
    scroller.classList.toggle('ty-tabs-scroller--wrap', wrap);

    const overflowLeft  = !wrap && track.scrollLeft > SLOP;
    const overflowRight = !wrap && (
      track.scrollLeft + track.clientWidth < track.scrollWidth - SLOP
    );
    scroller.classList.toggle('is-overflow-left',  overflowLeft);
    scroller.classList.toggle('is-overflow-right', overflowRight);

    const topbar = scroller.closest('.ty-topbar');
    if (topbar && narrow) {
      topbar.style.setProperty('--ty-chrome-h', `${topbar.offsetHeight}px`);
    } else if (topbar) {
      topbar.style.removeProperty('--ty-chrome-h');
    }
  }, []);

  // Initial measurement + react to anything that can change overflow:
  //   * window resize
  //   * tab list change (e.g. user switches app, different tab count)
  //   * a font / layout shift inside an individual tab
  useEffect(() => {
    recomputeOverflow();
    const track = trackRef.current;
    if (!track) return undefined;

    window.addEventListener('resize', recomputeOverflow);
    track.addEventListener('scroll', recomputeOverflow, { passive: true });

    // ResizeObserver catches layout shifts that don't fire 'resize'
    // (e.g. another flex sibling growing).  Guarded for old browsers
    // even though every target we care about supports it.
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(recomputeOverflow);
      ro.observe(track);
    }

    return () => {
      window.removeEventListener('resize', recomputeOverflow);
      track.removeEventListener('scroll', recomputeOverflow);
      if (ro) ro.disconnect();
    };
  }, [recomputeOverflow, tabs]);

  // After the route changes, the active tab may now be outside the
  // visible area — pull it into view.  Defer one tick so the
  // ``ty-tab--active`` class has been applied first.
  const location = useLocation();
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const track = trackRef.current;
      if (!track) return;
      const active = track.querySelector('.ty-tab--active');
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
      recomputeOverflow();
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, recomputeOverflow]);

  const scrollByAmount = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    // Page by ~70% of the visible width, capped at 320px so the
    // arrows feel responsive on very wide screens.
    const step = Math.min(Math.max(track.clientWidth * 0.7, 120), 320);
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const [openKey, setOpenKey] = useState(null);
  useEffect(() => { setOpenKey(null); }, [location.pathname]);

  if (!tabs || tabs.length === 0) return null;

  return (
    <div className="ty-tabs-scroller" ref={scrollerRef}>
      <button
        type="button"
        className="ty-tabs-scroller__arrow ty-tabs-scroller__arrow--left"
        aria-label="Scroll tabs left"
        tabIndex={-1}
        onClick={() => scrollByAmount(-1)}
      >
        ‹
      </button>

      <nav className="ty-tabs" aria-label={ariaLabel} ref={trackRef}>
        {tabs.map((tab) => (
          Array.isArray(tab.children) && tab.children.length ? (
            <TabMenu
              key={tabKey(tab)}
              tab={tab}
              isTabEnd={isTabEnd}
              open={openKey === tabKey(tab)}
              onToggle={() => setOpenKey((current) => (
                current === tabKey(tab) ? null : tabKey(tab)
              ))}
              onClose={() => setOpenKey(null)}
            />
          ) : (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={isTabEnd(tab.to)}
              className={({ isActive }) =>
                'ty-tab' + (isActive ? ' ty-tab--active' : '')
              }
            >
              {tab.label}
            </NavLink>
          )
        ))}
      </nav>

      <button
        type="button"
        className="ty-tabs-scroller__arrow ty-tabs-scroller__arrow--right"
        aria-label="Scroll tabs right"
        tabIndex={-1}
        onClick={() => scrollByAmount(1)}
      >
        ›
      </button>
    </div>
  );
}



/**
 * AppShell — Zoho-One-style chrome that wraps every authenticated page.
 *
 * Layout (desktop / tablet)
 * -------------------------
 *
 *     ┌─APP RAIL─┬───────────── TOP BAR ─────────────────────┐
 *     │   📋      │  Tasks · My Tasks  My Team  Create … User │
 *     │   📨      ├──────────────────────────────────────────┤
 *     │   💬      │                                          │
 *     │           │              <Outlet />                  │
 *     │  (logout) │                                          │
 *     │           ├──────────────────────────────────────────┤
 *     │           │  💬 Chat with us             ❓ Support  │
 *     └───────────┴──────────────────────────────────────────┘
 *
 *   * APP RAIL  (~56px) — one tile per app from `NAV`.
 *   * TOP BAR   (~40px) — current app's "pages" + user controls.
 *   * BOTTOM    (~32px) — global Chat / Support shortcuts.
 *
 * Mobile (<600px)
 * ---------------
 * The rail collapses; a "≡" hamburger in the top bar opens an
 * overlay sheet listing every app from `NAV`, with the current
 * app's pages nested under it.  Module tabs sit on their own
 * full-width band under the title (wrapping when the list is short
 * enough that every label fits) so later pages such as Form
 * Builder's Audience, Figures, Reports and Report templates stay
 * on screen.  The bottom Chat / Support bar stays.
 */
export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [appMenuOpen, setAppMenuOpen] = useState(false);

  // Auto-close the mobile app-menu whenever we navigate.
  useEffect(() => { setAppMenuOpen(false); }, [location.pathname]);

  // Which app is "active" — drives the highlight on the rail and the
  // tabs shown on the top bar.
  const current = useMemo(() => activeApp(location.pathname), [location.pathname]);

  // Filter NAV down to the apps this user (on this host) is allowed
  // to open.  This is what stops:
  //   * a super-admin on tasky.do from seeing the Tasks/Complaints
  //     tiles (whose APIs aren't even mounted on the public schema —
  //     clicking them was producing 404s),
  //   * an org user without "Complaints" permission from seeing the
  //     Complaints tile they aren't allowed to use.
  // ``canAccessApp`` is the same function ``RequirePermission`` uses,
  // so the rail and the route guard agree on what's reachable.
  const visibleNav = useMemo(
    () => navForProduct(NAV, (getConfig()?.APP?.productKey) || APP.productKey || 'taskydo')
      .filter((app) => {
        if (canAccessApp(app, user)) return true;
        // An explicit None still shows the link. Opening it explains
        // that an admin has to grant the module.
        return user?.module_access?.[app.appKey] === 'none';
      }),
    [user],
  );

  // Whether the global Chat shortcut at the bottom of the shell should
  // render.  Same gate as the side-rail tile.
  const chatVisible = useMemo(
    () => visibleNav.some((app) => app.appKey === 'chat'),
    [visibleNav],
  );

  // Sub-page links for the active app, filtered down to the ones this
  // user's per-module access level permits.  A "View only" user on
  // Tasks, for example, still sees Dashboard / My Tasks but NOT the
  // "Create Task" link (which carries ``minAccess: 'user'`` in the NAV
  // config).  See ``visibleChildren`` / ``hasModuleAccess`` in
  // ``config.js``.  UX gating only — the backend still enforces writes.
  const currentTabs = useMemo(
    () => visibleChildren(current, user),
    [current, user],
  );

  // For top-bar tabs we use `end` matching for tab anchors that ARE the
  // app's home path so they don't stay active on every sub-page.
  const isTabEnd = (to) =>
    to === current.home ||
    NAV.some((app) => app.urlPrefix === to);



  // Initials for the top-right avatar fallback — first letter of the
  // first name + first letter of the last name (per the brief).  Falls
  // back to the first character of the full name / email when a user
  // hasn't filled in both name fields.
  const initials = useMemo(() => {
    if (!user) return '?';
    const first = (user.first_name || '').trim();
    const last = (user.last_name || '').trim();
    const combined = (first.charAt(0) + last.charAt(0)).trim();
    if (combined) return combined.toUpperCase();
    const src = (user.full_name || user.email || '?').toString().trim();
    return src.charAt(0).toUpperCase() || '?';
  }, [user]);

  const displayName = user
    ? (user.full_name || user.first_name || user.email)
    : '';


  return (
    <div className={`ty-shell ${appMenuOpen ? 'ty-shell--menu-open' : ''}`}>

      {/* =========================== LEFT APP RAIL ========================== */}
      <aside className="ty-app-rail" aria-label="Apps">
        {/* Brand mark at the top of the rail — gradient tile with the
            Taskydo checkmark.  The actual SVG lives in
            ``components/Icons.jsx`` so the favicon and the in-app brand
            mark stay in lock-step. */}
        <div
          aria-hidden
          title={APP.name}
          style={{
            width: 36,
            height: 36,
            margin: '4px auto 10px',
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
            filter: 'drop-shadow(0 6px 14px rgba(121, 128, 255, 0.35))',
          }}
        >
          <BrandMark size={36} rounded={10} />
        </div>

        {visibleNav.map((app) => (
          <button
            key={app.module}
            type="button"
            title={app.label}
            aria-label={app.label}
            aria-current={current.module === app.module ? 'page' : undefined}
            className={
              'ty-app-rail__btn' +
              (current.module === app.module ? ' ty-app-rail__btn--active' : '')
            }
            onClick={() => navigate(app.home)}
          >
            <span className="ty-app-rail__icon" aria-hidden>
              <AppIcon name={app.icon} size={22} />
            </span>
            <span className="ty-app-rail__caption">{app.label}</span>
          </button>
        ))}


        <div className="ty-app-rail__spacer" />

        <button
          type="button"
          title="Logout"
          aria-label="Logout"
          className="ty-app-rail__btn ty-app-rail__btn--muted"
          onClick={logout}
        >
          <span className="ty-app-rail__icon" aria-hidden>
            <LogoutIcon size={22} />
          </span>
          <span className="ty-app-rail__caption">Logout</span>
        </button>
      </aside>

      {/* ============================== TOP BAR ============================= */}
      <header className="ty-topbar">
        <button
          type="button"
          className="ty-hamburger"
          aria-label="Open app menu"
          aria-expanded={appMenuOpen}
          onClick={() => setAppMenuOpen((o) => !o)}
        >
          <span /><span /><span />
        </button>

        <Link to={current.home} className="ty-topbar__title">
          <span className="ty-topbar__app-icon" aria-hidden>
            <AppIcon name={current.icon} size={18} />
          </span>
          <span className="ty-topbar__app-name">{current.label}</span>
        </Link>

        <ScrollableTabs
          tabs={currentTabs}
          isTabEnd={isTabEnd}
          ariaLabel={`${current.label} sections`}
        />


        <div className="ty-topbar__spacer" />

        <AppSwitcher user={user} />

        {/* Notification "bell" — aggregates Tasks / Routine Tasks /
            Complaints notifications for the current user.  Backed by
            ``GET /api/notification/inbox/``.  Renders its own dropdown
            panel and unread badge; see ``NotificationBell.jsx``. */}
        <NotificationBell />

        {/* User area — clicking the name / avatar opens a dropdown with
            "My Profile", "Change Password" and "Logout".  All of the
            identity + avatar rendering (and the menu behaviour) lives in
            ``UserMenu`` so this shell stays focused on layout. */}
        <UserMenu />
      </header>



      {/* ====================== MOBILE APP-MENU OVERLAY ===================== */}
      {/* Visible only on small screens — see global.css for the breakpoint. */}
      <div
        className="ty-app-menu-backdrop"
        onClick={() => setAppMenuOpen(false)}
        role="presentation"
      />
      <aside className="ty-app-menu" aria-label="Apps menu" aria-hidden={!appMenuOpen}>
        <header className="ty-app-menu__header">
          <span className="ty-app-menu__brand">{APP.name}</span>
          <button
            type="button"
            className="ty-app-menu__close"
            onClick={() => setAppMenuOpen(false)}
            aria-label="Close menu"
          >×</button>
        </header>
        <ul className="ty-app-menu__list">
          {visibleNav.map((app) => (
            <li key={app.module}>
              <button
                type="button"
                className={
                  'ty-app-menu__item' +
                  (current.module === app.module ? ' ty-app-menu__item--active' : '')
                }
                onClick={() => navigate(app.home)}
              >
                <span className="ty-app-menu__icon" aria-hidden>
                  <AppIcon name={app.icon} size={20} />
                </span>
                <span className="ty-app-menu__label">{app.label}</span>
              </button>
              {current.module === app.module && currentTabs.length > 0 && (
                <ul className="ty-app-menu__sections">
                  {currentTabs.map((tab) => (
                    Array.isArray(tab.children) && tab.children.length ? (
                      <li key={tabKey(tab)}>
                        <div className="ty-app-menu__group">{tab.label}</div>
                        <ul className="ty-app-menu__sections">
                          {tab.children.map((child) => (
                            <li key={child.to}>
                              <NavLink
                                to={child.to}
                                end={linkShouldEnd(
                                  location.pathname,
                                  child.to,
                                  peerPaths(tab),
                                  isTabEnd(child.to),
                                )}
                                className={({ isActive }) =>
                                  'ty-app-menu__section ty-app-menu__section--nested'
                                  + (isActive ? ' ty-app-menu__section--active' : '')
                                }
                                onClick={() => setAppMenuOpen(false)}
                              >
                                {child.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ) : (
                      <li key={tab.to}>
                        <NavLink
                          to={tab.to}
                          end={isTabEnd(tab.to)}
                          className={({ isActive }) =>
                            'ty-app-menu__section'
                            + (isActive ? ' ty-app-menu__section--active' : '')
                          }
                          onClick={() => setAppMenuOpen(false)}
                        >
                          {tab.label}
                        </NavLink>
                      </li>
                    )
                  ))}
                </ul>
              )}
            </li>
          ))}

          {/* Settings / My Profile — lets mobile users reach the page
              where they can change their name, mobile, email and
              picture (the rail hosts the same link via the avatar on
              wider screens). */}
          <li>
            <button
              type="button"
              className="ty-app-menu__item"
              onClick={() => navigate(ROUTES.PROFILE)}
            >
              <span className="ty-app-menu__icon" aria-hidden>
                <Avatar
                  initials={initials}
                  imageUrl={user?.profile_image_url || ''}
                  name={displayName}
                  size={20}
                />
              </span>
              <span className="ty-app-menu__label">Settings</span>
            </button>
          </li>

          <li>
            <button
              type="button"
              className="ty-app-menu__item ty-app-menu__item--muted"
              onClick={logout}
            >
              <span className="ty-app-menu__icon" aria-hidden>
                <LogoutIcon size={20} />
              </span>
              <span className="ty-app-menu__label">Logout</span>
            </button>
          </li>
        </ul>
      </aside>


      {/* ============================== MAIN ================================ */}
      <main className="ty-main" id="main">
        <Outlet />
      </main>

      {/* ============================ BOTTOM BAR ============================ */}
      <footer className="ty-footerbar">
        {chatVisible && (
          <button
            type="button"
            className="ty-footerbar__btn"
            onClick={() => navigate('/chat')}
          >
            <span aria-hidden style={{ display: 'inline-flex' }}>
              <ChatBubbleIcon size={16} />
            </span>
            <span>Chat</span>
          </button>
        )}
        <div className="ty-footerbar__spacer" />
        <button
          type="button"
          className="ty-footerbar__btn"
          onClick={() => window.open('mailto:support@tasky.do', '_blank')}
        >
          <span aria-hidden style={{ display: 'inline-flex' }}>
            <SupportIcon size={16} />
          </span>
          <span>Support</span>
        </button>
      </footer>

    </div>
  );
}
